import React from 'react';
import { TrendingUp, FileText, Send, Upload, Home, Clock, Phone, PhoneCall } from 'lucide-react';
import { MetricTile } from './MetricTile';
import { motion } from 'framer-motion';
import { LoanStage } from '../data/loans';
import { OfficerData } from '../data/officers';
interface MonthlyScorecardProps {
  officer: OfficerData;
  onTileClick?: (stage: LoanStage) => void;
  onSpeedBonusClick?: () => void;
  onAnswerRateClick?: () => void;
  onDialsClick?: () => void;
}
export function MonthlyScorecard({
  officer,
  onTileClick,
  onSpeedBonusClick,
  onAnswerRateClick,
  onDialsClick
}: MonthlyScorecardProps) {
  const {
    monthlyPoints,
    monthlyGoal,
    creditPulls,
    docOuts,
    submissions,
    closings,
    speedBonusFastCloses,
    speedBonusPoints
  } = officer;
  const remaining = Math.max(0, monthlyGoal - monthlyPoints);
  const percent = Math.round(monthlyPoints / monthlyGoal * 1000) / 10;
  const qualified = monthlyPoints >= monthlyGoal;
  const barWidth = Math.min(100, percent);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.2
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full" data-id="element-385">
      <div className="bg-[#0a1e5e] px-4 py-3 flex items-center justify-between gap-3 text-white" data-id="element-386">
        <div className="flex items-center gap-2 min-w-0" data-id="element-387">
          <TrendingUp className="w-5 h-5 shrink-0" data-id="element-388" />
          <h2 className="font-bold tracking-wide truncate" data-id="element-389">
            MONTHLY SCORECARD
          </h2>
        </div>
        <span className="text-xs sm:text-sm font-medium text-blue-200 shrink-0" data-id="element-390">
          May 1 – May 31, 2025
        </span>
      </div>

      <div className="p-5 flex-1 flex flex-col" data-id="element-391">
        {/* Top Stats */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mb-6" data-id="element-392">
          <div className="flex-1 min-w-0" data-id="element-393">
            <p className="text-xs font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-394">
              Total Points (Month To Date)
            </p>
            <div className="flex items-baseline gap-2 flex-wrap" data-id="element-395">
              <span className={`text-5xl sm:text-6xl font-extrabold tracking-tight leading-none ${qualified ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-396">
                {monthlyPoints.toLocaleString()}
              </span>
              <span className="text-sm font-bold text-gray-500" data-id="element-397">POINTS</span>
            </div>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-398">
            <div className="flex justify-between items-center gap-2 mb-2 pb-2 border-b border-gray-200" data-id="element-399">
              <span className="text-xs font-bold text-gray-600 uppercase" data-id="element-400">
                Goal
              </span>
              <div className="flex items-baseline gap-1 shrink-0" data-id="element-401">
                <span className="text-lg font-extrabold text-[#0a1e5e]" data-id="element-402">
                  {monthlyGoal.toLocaleString()}
                </span>
                <span className="text-[10px] font-bold text-gray-500" data-id="element-403">PTS</span>
              </div>
            </div>
            <div className="flex justify-between items-center gap-2 mb-3" data-id="element-404">
              <span className="text-xs font-bold text-gray-600 uppercase" data-id="element-405">
                Remaining
              </span>
              <div className="flex items-baseline gap-1 shrink-0" data-id="element-406">
                <span className={`text-lg font-extrabold ${qualified ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-407">
                  {remaining.toLocaleString()}
                </span>
                <span className="text-[10px] font-bold text-gray-500" data-id="element-408">PTS</span>
              </div>
            </div>
            <div data-id="element-409">
              <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-410">
                % Of Goal Completed
              </p>
              <p className={`text-xl font-extrabold leading-none ${qualified ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-411">
                {percent}%
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8" data-id="element-412">
          <div className="flex justify-between items-end mb-2" data-id="element-413">
            <span className="text-xs font-bold text-[#0a1e5e] uppercase tracking-wider" data-id="element-414">
              Monthly Progress
            </span>
            <span className={`text-sm font-bold ${qualified ? 'text-green-600' : 'text-blue-600'}`} data-id="element-415">
              {percent}%{qualified ? ' · Goal Met 🎉' : ''}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden" data-id="element-416">
            <div className={`h-4 rounded-full relative ${qualified ? 'bg-green-600' : 'bg-blue-600'}`} style={{
            width: `${barWidth}%`
          }} data-id="element-417">
              <div className="absolute right-0 top-0 bottom-0 w-4 bg-white/30" data-id="element-418"></div>
            </div>
          </div>
        </div>

        <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-4" data-id="element-419">
          Monthly Breakdown
        </h3>

        {/* Metric Tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-id="element-420">
          <MetricTile title="Call Answer Rate" points={0} count={officer.callAnswerRate} countSuffix="%" countLabel="Answer Rate" icon={Phone} colorClass="text-teal-600" bgClass="bg-teal-100" hidePoints onClick={onAnswerRateClick} data-id="element-421" />
          <MetricTile title="Dials" points={0} count={officer.dials} countLabel="Dials" icon={PhoneCall} colorClass="text-indigo-600" bgClass="bg-indigo-100" hidePoints onClick={onDialsClick} data-id="element-422" />
          <MetricTile title="Credit Pulls" points={750} count={creditPulls} countLabel="Pulls" icon={FileText} colorClass="text-blue-700" bgClass="bg-blue-100" ptsPerAction={750} onClick={onTileClick ? () => onTileClick('credit_pull') : undefined} data-id="element-423" />
          <MetricTile title="Doc Outs" points={500} count={docOuts} countLabel="Doc Outs" icon={Send} colorClass="text-green-600" bgClass="bg-green-100" ptsPerAction={500} onClick={onTileClick ? () => onTileClick('doc_out') : undefined} data-id="element-424" />
          <MetricTile title="Submissions" points={500} count={submissions} countLabel="Submissions" icon={Upload} colorClass="text-purple-600" bgClass="bg-purple-100" ptsPerAction={500} onClick={onTileClick ? () => onTileClick('submission') : undefined} data-id="element-425" />
          <MetricTile title="Closings" points={250} count={closings} countLabel="Closings" icon={Home} colorClass="text-orange-500" bgClass="bg-orange-100" ptsPerAction={250} onClick={onTileClick ? () => onTileClick('closing') : undefined} data-id="element-426" />
        </div>

        {/* Speed Bonus MTD */}
        {(() => {
        const Wrapper: any = onSpeedBonusClick ? 'button' : 'div';
        return <Wrapper {...onSpeedBonusClick ? {
          type: 'button',
          onClick: onSpeedBonusClick,
          'aria-label': 'View speed bonus loans this month'
        } : {}} className={`group relative mt-auto w-full bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between gap-3 transition-all text-left ${onSpeedBonusClick ? 'hover:border-orange-400 hover:shadow-sm hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400' : ''}`} data-id="element-427">
              <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-428">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center shrink-0" data-id="element-429">
                  <Clock className="w-6 h-6 text-white" data-id="element-430" />
                </div>
                <div className="min-w-0" data-id="element-431">
                  <p className="text-sm font-bold text-[#0a1e5e] uppercase leading-tight" data-id="element-432">
                    Speed Bonus{' '}
                    <span className="text-gray-500 text-[10px] font-semibold" data-id="element-433">
                      (MTD)
                    </span>
                  </p>
                  <p className="text-xs text-gray-600 leading-tight mt-0.5" data-id="element-434">
                    Close a loan within 14 days of credit pull
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0" data-id="element-435">
                <div className="flex items-baseline gap-1 justify-end" data-id="element-436">
                  <span className="text-2xl sm:text-3xl font-extrabold text-orange-500 leading-none" data-id="element-437">
                    +{speedBonusPoints.toLocaleString()}
                  </span>
                  <span className="text-sm font-bold text-gray-500" data-id="element-438">PTS</span>
                </div>
                <p className="text-[10px] font-bold text-[#0a1e5e] uppercase mt-1" data-id="element-439">
                  {speedBonusFastCloses} Fast{' '}
                  {speedBonusFastCloses === 1 ? 'Close' : 'Closes'}
                </p>
              </div>
              {onSpeedBonusClick && <span className="absolute top-2 right-2 text-[10px] font-bold text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider" data-id="element-440">
                  View loans →
                </span>}
            </Wrapper>;
      })()}
      </div>
    </motion.div>;
}